import { isActivityExportJobType } from './identity';
import { updateExistingActivityItem } from './item-update';
import {
    hasConflictingActivityLineageBuildingIds,
    quarantineConflictingActivityLineages,
    stableActivityBuildingPublicId,
    toActivityJob,
} from './job';
import {
    createEmptyActivitySession,
    isActivityBatchKey,
    type ActivityItem,
    type ActivityJobUpdate,
    type ActivityKey,
    type ActivitySession,
} from './models';
import {
    activityIntentFor,
    activitySectionFor,
    canDismissActivity,
    isActivityTerminal,
    isAttentionActivitySection,
} from './policy';
import { reconcileSharePointPlaceholderProvenance } from './sharepoint-placeholder';
import {
    boundActivitySession,
    isActivityIntegrityQuarantined,
} from './storage-limits';

export type ActivityAction =
    | { readonly type: 'job-observed'; readonly update: ActivityJobUpdate }
    | {
          readonly type: 'mark-attention-seen';
          readonly buildingPublicId: string | null;
      }
    | { readonly type: 'mark-acted-on'; readonly key: ActivityKey }
    | { readonly type: 'dismiss'; readonly key: ActivityKey }
    | { readonly type: 'mark-stale'; readonly key?: ActivityKey }
    | { readonly type: 'mark-unavailable'; readonly key: ActivityKey }
    | { readonly type: 'mark-available'; readonly key: ActivityKey }
    | { readonly type: 'quarantine'; readonly key: ActivityKey }
    | { readonly type: 'clear' };

export function reduceActivity(
    state: ActivitySession,
    action: ActivityAction
): ActivitySession {
    switch (action.type) {
        case 'job-observed':
            return observeJob(state, action.update);
        case 'mark-attention-seen':
            return mapChangedItems(state, (item) =>
                isAttentionActivitySection(activitySectionFor(item)) &&
                !item.seen &&
                (action.buildingPublicId === null ||
                    stableActivityBuildingPublicId(item.jobs) ===
                        action.buildingPublicId)
                    ? { ...item, seen: true }
                    : item
            );
        case 'mark-acted-on':
            return mapChangedItems(state, (item) =>
                item.key === action.key &&
                activityIntentFor(item) !== null &&
                !item.actedOn
                    ? { ...item, actedOn: true }
                    : item
            );
        case 'dismiss':
            return dismissItem(state, action.key);
        case 'mark-stale':
            return setAvailability(state, action.key, 'stale');
        case 'mark-available':
            return setAvailability(state, action.key, 'available');
        case 'mark-unavailable':
            return setAvailability(state, action.key, 'unavailable');
        case 'quarantine':
            return quarantineItem(state, action.key);
        case 'clear':
            return createEmptyActivitySession();
    }
}

function quarantineItem(
    state: ActivitySession,
    key: ActivityKey
): ActivitySession {
    return mapChangedItems(state, (item) =>
        item.key === key &&
        (item.availability !== 'unavailable' ||
            item.identityQuarantined !== true ||
            item.seen)
            ? {
                  ...item,
                  availability: 'unavailable',
                  identityQuarantined: true,
                  seen: false,
              }
            : item
    );
}

function observeJob(
    state: ActivitySession,
    update: ActivityJobUpdate
): ActivitySession {
    if (
        isActivityExportJobType(update.jobType) &&
        update.key === `job:${update.jobId}` &&
        state.dismissalTombstoneMetadata.some((metadata) =>
            metadata.observedJobIds.includes(update.jobId)
        )
    ) {
        return state;
    }

    if (state.dismissalTombstones.includes(update.key)) {
        const revived = reviveDismissedBatch(state, update);
        if (!revived) return state;
        state = revived;
    }

    const itemIndex = state.items.findIndex((item) => item.key === update.key);
    const existing = itemIndex === -1 ? null : state.items[itemIndex];
    const preserveLineageBuildingIdentity =
        update.correlationId !== null &&
        hasConflictingActivityLineageBuildingIds(
            state.items,
            update.correlationId
        );
    const nextItem = existing
        ? updateExistingActivityItem(
              existing,
              update,
              preserveLineageBuildingIdentity
          )
        : createItem(update);

    if (nextItem === existing) {
        return state;
    }

    let items: readonly ActivityItem[] =
        itemIndex === -1
            ? [...state.items, nextItem]
            : state.items.map((item, index) =>
                  index === itemIndex ? nextItem : item
              );
    items = quarantineConflictingActivityLineages(items);
    items = reconcileSharePointPlaceholderProvenance(items);

    return boundActivityState({
        ...state,
        items,
    });
}

function createItem(update: ActivityJobUpdate): ActivityItem {
    const item: ActivityItem = {
        key: update.key,
        correlationId: update.correlationId,
        jobs: [toActivityJob(update)],
        observedJobIds: [update.jobId],
        createdAtUtc: update.occurredAtUtc,
        updatedAtUtc: update.occurredAtUtc,
        seen: false,
        actedOn: false,
        availability: 'available',
        identityQuarantined: false,
    };

    return item;
}

function dismissItem(
    state: ActivitySession,
    key: ActivityKey
): ActivitySession {
    const item = state.items.find((candidate) => candidate.key === key);

    if (!item || !canDismissActivity(item)) {
        return state;
    }

    return boundActivityState({
        ...state,
        items: state.items.filter((candidate) => candidate.key !== key),
        dismissalTombstones: state.dismissalTombstones.includes(key)
            ? state.dismissalTombstones
            : [...state.dismissalTombstones, key],
        dismissalTombstoneMetadata: isActivityBatchKey(key)
            ? [
                  ...state.dismissalTombstoneMetadata.filter(
                      (metadata) => metadata.key !== key
                  ),
                  {
                      key,
                      observedJobIds: observedJobIdsFor(item),
                      latestObservedAtUtc: item.updatedAtUtc,
                  },
              ]
            : state.dismissalTombstoneMetadata,
    });
}

function reviveDismissedBatch(
    state: ActivitySession,
    update: ActivityJobUpdate
): ActivitySession | null {
    if (!isActivityBatchKey(update.key)) return null;

    const metadata = state.dismissalTombstoneMetadata.find(
        (candidate) => candidate.key === update.key
    );
    if (
        !metadata ||
        metadata.observedJobIds.includes(update.jobId) ||
        Date.parse(update.occurredAtUtc) <=
            Date.parse(metadata.latestObservedAtUtc)
    ) {
        return null;
    }

    return {
        ...state,
        dismissalTombstones: state.dismissalTombstones.filter(
            (key) => key !== update.key
        ),
        dismissalTombstoneMetadata: state.dismissalTombstoneMetadata.filter(
            (candidate) => candidate.key !== update.key
        ),
    };
}

function observedJobIdsFor(item: ActivityItem): readonly string[] {
    return item.observedJobIds ?? item.jobs.map((job) => job.jobId);
}

function boundActivityState(state: ActivitySession): ActivitySession {
    return boundActivitySession(state);
}

function setAvailability(
    state: ActivitySession,
    key: ActivityKey | undefined,
    availability: ActivityItem['availability']
): ActivitySession {
    return mapChangedItems(state, (item) => {
        if (
            (key && item.key !== key) ||
            item.availability === availability ||
            (availability === 'available' &&
                isActivityIntegrityQuarantined(item)) ||
            (availability === 'stale' &&
                isActivityTerminal(item) &&
                !item.jobs.some(
                    (job) => job.jobType === 'SelectedBuildingDocumentExport'
                ))
        ) {
            return item;
        }

        return {
            ...item,
            availability,
            seen: availability === 'unavailable' ? false : item.seen,
        };
    });
}

function mapChangedItems(
    state: ActivitySession,
    mapper: (item: ActivityItem) => ActivityItem
): ActivitySession {
    let changed = false;
    const items = state.items.map((item) => {
        const next = mapper(item);
        changed ||= next !== item;
        return next;
    });

    return changed ? { ...state, items } : state;
}
